function stitchingRoutes() {
  const r = typeof Theme !== 'undefined' ? Theme.routes : {};
  const root_url = r.root_url || '';
  /** @type {string} */
  let all_products = r.all_products_collection_url || '/collections/all';
  if (all_products.endsWith('/')) {
    all_products = all_products.slice(0, -1); 
  }
  return { root_url, all_products_collection_url: all_products };
}

function variantImageSrc(variant) {
  if (!variant) return '';
  if (variant.featured_image?.src) return variant.featured_image.src;
  const im = variant.image;
  if (typeof im === 'string') return im;
  return im?.src || '';
}

function productFeaturedSrc(product) {
  const fi = product.featured_image;
  if (typeof fi === 'string') return fi;
  return fi?.src || '';
}

/** Liquid JSON image fields (`featured_image`, `second_image`) can be a string URL or `{ src }`. */
function productImageSrcFromLiquidJson(img) {
  if (img == null) return '';
  if (typeof img === 'string') return img;
  if (typeof img === 'object' && 'src' in img && typeof /** @type {{ src?: string }} */ (img).src === 'string') {
    return /** @type {{ src: string }} */ (img).src;
  }
  return '';
}

/**
 * Shopify CDN URLs often differ only by query; treat same path as one image.
 * @param {string} url
 */
function imagePathKey(url) {
  if (!url) return '';
  try {
    const u =
      url.startsWith('//') ? new URL(`https:${url}`)
      : /^https?:\/\//i.test(url) ? new URL(url)
      : new URL(url, 'https://cdn.shopify.com');
    return `${u.hostname}${u.pathname}`;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

/**
 * Second PLP hover image from `templates/collection.json.liquid` (`second_image` / `images[1]`).
 * @param {Record<string, unknown>} product
 * @param {string} primarySrc
 */
function productSecondHoverSrc(product, primarySrc) {
  let s = productImageSrcFromLiquidJson(product.second_image);
  if (!s || imagePathKey(s) === imagePathKey(primarySrc)) return '';
  return s;
}

const stitchingCache = new Map();

function fetchStitchingJson(settings) {
  const { all_products_collection_url } = stitchingRoutes();
  if (!settings.groupTag || settings.groupTag === 'undefined') {
    return Promise.reject(new Error('colour-stitching: missing groupTag'));
  }
  const url = `${all_products_collection_url}/${encodeURIComponent(settings.groupTag)}?view=json`;
  // return fetch(url).then((response) => response.json());
  if (!stitchingCache.has(url)) {
    stitchingCache.set(
      url,
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.json();
        })
        .catch((err) => {
          stitchingCache.delete(url);
          throw err;
        })
    );
  }
  return stitchingCache.get(url);
}

function readSettings(self) {
  if (self.hasAttribute('data-group-tag')) {
    return {
      id: self.getAttribute('data-product-id') ? Number(self.getAttribute('data-product-id')) : null,
      handle: self.getAttribute('data-handle'),
      groupTag: self.getAttribute('data-group-tag'),
      image_type: self.getAttribute('data-image-type')
    };
  }
  const script = self.querySelector('script[type="application/json"][data-colour-stitch-settings]');
  if (!script?.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

function waitForSettings(self) {
  const instantSettings = readSettings(self);
  if (instantSettings) {
    return Promise.resolve(instantSettings);
  }
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      const s = readSettings(self);
      if (s) {
        clearInterval(interval);
        resolve(s);
      }
    }, 100);
    setTimeout(() => {
      clearInterval(interval);
      resolve(readSettings(self));
    }, 3000);
  });
}

function findCardGallery(settings) {
  return document.querySelector(`.card-gallery[data-product-id="${settings.id}"]`);
}

/**
 * Instant colourway switch — show new before hiding old so nothing flashes underneath.
 *
 * @param {HTMLElement} gallery
 * @param {string} [stitchHandle]
 * @returns {boolean}
 */
function selectStitchColourway(gallery, stitchHandle) {
  /** @type {HTMLElement | null} */
  let next = null;

  if (stitchHandle) {
    const esc =
      typeof CSS !== 'undefined' && CSS.escape
        ? CSS.escape(stitchHandle)
        : stitchHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const found = gallery.querySelector(`.colour-stitching__colourway[data-stitch-handle="${esc}"]`);
    if (found instanceof HTMLElement) next = found;
  }

  if (!next) {
    const fallback = gallery.querySelector('.colour-stitching__colourway');
    if (fallback instanceof HTMLElement) next = fallback;
  }

  if (!next) return false;

  const prev = gallery.querySelector('.colour-stitching__colourway.selected');
  if (prev === next) return true;

  next.classList.add('selected');
  if (prev instanceof HTMLElement) prev.classList.remove('selected');
  return true;
}

/**
 * @param {Record<string, unknown> | undefined} product
 * @param {string} stitchHandle
 * @param {string} baseHandle
 * @param {string | null} originalUrlAttr
 */
function resolveStitchedCardHref(product, stitchHandle, baseHandle, originalUrlAttr) {
  const sh = (stitchHandle || '').toString();
  const bh = (baseHandle || '').toString();
  if (sh === bh && originalUrlAttr) return originalUrlAttr;
 
  let raw = product && typeof product.url === 'string' ? product.url : '';
  if (raw && originalUrlAttr && originalUrlAttr.startsWith('/collections/')) {
    const match = originalUrlAttr.match(/^(\/collections\/[^\/]+)/);
    if (match) {
      raw = match[1] + raw;
    }
  }
  if (!raw) return originalUrlAttr || '';

  /** @type {string} */
  let pathPlusSearch;
  try {
    const u = raw.startsWith('http') ? new URL(raw) : new URL(raw, window.location.origin);
    pathPlusSearch = `${u.pathname}${u.search}`;
  } catch {
    pathPlusSearch = raw.startsWith('/') ? raw : `/${raw}`;
  }

  const variants = product && Array.isArray(product.variants) ? /** @type {Record<string, unknown>[]} */ (product.variants) : [];
  const vid = variants[0]?.id;
  if (vid != null && pathPlusSearch && !pathPlusSearch.includes('variant=')) {
    pathPlusSearch += (pathPlusSearch.includes('?') ? '&' : '?') + `variant=${vid}`;
  }

  return pathPlusSearch;
}

/**
 * @param {HTMLElement} card
 * @param {HTMLElement} gallery
 * @param {string} stitchHandle
 * @param {Record<string, unknown> | undefined} product
 * @param {Record<string, unknown>} settings
 */
function applyStitchNavigationToCard(card, gallery, stitchHandle, product, settings) {
  gallery.dataset.activeStitchHandle = (stitchHandle || '').toString();

  const baseHandle = ((settings.handle || '') + '').toString();
  const originalUrlAttr = card.getAttribute('data-stitch-original-url');
  const path = resolveStitchedCardHref(product, stitchHandle, baseHandle, originalUrlAttr);

  if (!path) return;

  let absolute;
  try {
    absolute =
      path.startsWith('http') ? path : new URL(path.startsWith('/') ? path : `/${path}`, window.location.origin).href;
  } catch {
    absolute = path;
  }

  /** @type {NodeListOf<HTMLAnchorElement> | null} */
  const mains = card.querySelectorAll('[ref="productCardLink"], a.product-card__link');
  mains.forEach((a) => {
    if (a instanceof HTMLAnchorElement) a.href = absolute;
  });

  /** @type {HTMLAnchorElement | null} */
  const galleryLink = gallery.querySelector('a[ref="cardGalleryLink"]');
  if (galleryLink instanceof HTMLAnchorElement) galleryLink.href = absolute;

  /** @type {HTMLAnchorElement | null} */
  const titleLink = card.querySelector('a[ref="productTitleLink"]');
  if (titleLink instanceof HTMLAnchorElement) titleLink.href = absolute;

  const ctaLink = card.closest('.compare-products__column')?.querySelector('.compare-products__cta a');
  if (ctaLink instanceof HTMLAnchorElement) ctaLink.href = absolute;

  if (product && card.getAttribute('data-product-transition') === 'true') {
    if (stitchHandle === baseHandle) {
      const origFeatured = card.dataset.stitchOrigFeaturedMediaUrl;
      if (origFeatured) card.setAttribute('data-featured-media-url', origFeatured);
    } else {
      const featSrc = productFeaturedSrc(product);
      if (featSrc) card.setAttribute('data-featured-media-url', featSrc);
    }
  }

  /** @type {{ refs?: { quickAdd?: { fetchProductPage: (url: string) => unknown }}} | null} */
  const pcAny = /** @type {*} */ (card);
  pcAny?.refs?.quickAdd?.prefetch?.();
}

class ColourSwatches extends HTMLElement {
  constructor() {
    super();
    // this.container = this.querySelector('.colour-stitching__container');
    // if (!this.container || this.classList.contains('locked')) return;
  }
  connectedCallback() {
    if (this._initialized) return;
    // Remove 'locked' in case the element was cloned from a currently fetching element.
    this.classList.remove('locked');


    waitForSettings(this).then((settings) => {
      // if (!this.container) return;
      this.container = this.querySelector('.colour-stitching__container');
      if (!this.container || this.classList.contains('locked') || this._initialized) return;
      if (settings == null) {
        console.warn('colour-stitch-plp: missing JSON settings');
        return;
      }

      this._initialized = true;
      this.classList.add('locked');

      fetchStitchingJson(settings)
        .then((responseJson) => {
          this.container.classList.add('loaded');
          if (!Array.isArray(responseJson)) {
            responseJson = responseJson.products;
          }
          if (!responseJson || responseJson.length < 1) {
            this.classList.add('hidden');
            this.nextElementSibling?.classList.remove('hidden');
          } else {
            if (window.location.href.search('/products') === -1) {
              this.nextElementSibling?.classList.add('hidden');
            }
            this._stitchProductsForCard = Array.isArray(responseJson) ? responseJson : [];
            this.initSwatches(responseJson, settings);
            this.initMainImages(responseJson, settings);
          }
          this.classList.remove('locked');
        })
        .catch((e) => {
          console.warn(e);
          this.classList.add('hidden');
          this.classList.remove('locked');
          this._initialized = false;
        });
    });
  }

  /**
   * @param {Record<string, unknown>[]} products
   * @param {Record<string, unknown>} settings
   */
  initMainImages(products, settings) {
    const scope = this.closest('product-card');
    const gallery =
      scope?.querySelector(`.card-gallery[data-product-id='${settings.id}']`) || findCardGallery(settings);
    if (!gallery) return;
    if (gallery.querySelector('.colour-stitching__image-layer .colour-stitching__colourway')) return;

    let html = '';
    for (const product of products) {
      const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
      const variant = variants[0];
      const handleStr = ((product.handle || '') + '').toString();
      const src = productFeaturedSrc(product) || variantImageSrc(variant);
      if (!src) continue;
      const secondSrc = productSecondHoverSrc(/** @type {Record<string, unknown>} */ (product), src);
      const hasSecond = secondSrc ? 'true' : 'false';
      const title =
        variant && typeof variant.title === 'string' ? variant.title : (product.handle || '').toString();
      const escTitle = title.replace(/"/g, '&quot;');
      const prim = `<img data-stitch-handle="${handleStr}" data-variant-id="${variant?.id ?? ''}" src="${src}" alt="${escTitle}" class="new-swatch-images new-swatch-images--primary" loading="eager" fetchpriority="low" width="600" height="600" decoding="async" />`;
      const sec = secondSrc
        ? `<img data-stitch-handle="${handleStr}" src="${secondSrc}" alt="" class="new-swatch-images new-swatch-images--secondary" loading="eager" fetchpriority="low" width="600" height="600" decoding="async" />`
        : '';
      html += `<div class="colour-stitching__colourway" data-stitch-handle="${handleStr}" data-has-second="${hasSecond}">${prim}${sec}</div>`;
    }

    /** @type {string} */
    const currentHandle = ((settings.handle || '') + '').toString();

    if (!html.trim()) return;

    if (scope instanceof HTMLElement && scope.getAttribute('data-product-transition') === 'true') {
      const curFeat = scope.getAttribute('data-featured-media-url');
      if (curFeat && !scope.dataset.stitchOrigFeaturedMediaUrl) {
        scope.dataset.stitchOrigFeaturedMediaUrl = curFeat;
      }
    }
    const firstSlideImg = gallery.querySelector('slideshow-slide:not([aria-hidden="true"]) img, slideshow-slide:first-child img');
    if (firstSlideImg instanceof HTMLImageElement) {
      firstSlideImg.classList.remove('blur-up', 'fade-in');
    }

    const layer =
      gallery.querySelector('.colour-stitching__image-layer') || document.createElement('div');
    if (!layer.classList.contains('colour-stitching__image-layer')) {
      layer.className = 'colour-stitching__image-layer';
      layer.setAttribute('aria-hidden', 'true');
      gallery.appendChild(layer);
    }
    layer.innerHTML = html;

    const escapedHandle =
      typeof CSS !== 'undefined' && CSS.escape
        ? CSS.escape(currentHandle)
        : currentHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const selected =
      currentHandle &&
      gallery.querySelector(`.colour-stitching__colourway[data-stitch-handle="${escapedHandle}"]`);
    /** @type {HTMLElement | null | undefined} */
    const fallback = layer.querySelector('.colour-stitching__colourway');

    /** @type {HTMLElement | undefined} */
    let toPick;
    if (selected instanceof HTMLElement) toPick = selected;
    else if (fallback instanceof HTMLElement) toPick = fallback;

    let overlayReady = selectStitchColourway(/** @type {HTMLElement} */ (gallery), currentHandle);
    if (!overlayReady && toPick instanceof HTMLElement) {
      layer.querySelectorAll('.colour-stitching__colourway').forEach((n) => n.classList.remove('selected'));
      toPick.classList.add('selected');
      overlayReady = true;
    }

    if (!overlayReady) return;

    gallery.classList.add('card-gallery--has-colour-stitching', 'card-gallery--stitch-overlay-ready');
    if (gallery.hasAttribute('on:pointerenter')) {
      gallery.classList.add('card-gallery--stitch-respects-hover-preview');
    }
    gallery.dataset.cardBaseHandle = currentHandle;
    gallery.dataset.activeStitchHandle = currentHandle;
  }

  /**
   * @param {Record<string, unknown>[]} products
   * @param {Record<string, unknown>} settings
   */
  initSwatches(products, settings) {
    let html = '';
    const cardRoot = this.closest('product-card');
    const intraVariantSwatches = cardRoot?.querySelector('product-swatches');

    products.forEach((product) => {
      const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
      const variant = variants[0];
      const img = this.getProductImage(product, settings);
      /** @type {string} */
      const handleStr = ((product.handle || '') + '').toString();
      const isSelectedClass = settings.handle === handleStr ? 'selected' : '';

      const hasNullPrice = product.price === 0 || product.price === undefined;

      const isAvailable = product.available !== false && product.available !== 'false';
      const disabledAttrs = isAvailable ? 'tabindex="0"' : 'tabindex="-1" aria-disabled="true"';
      const el = `<div class="colour-stitching__swatch-item thumbnail ${isSelectedClass}" role="button" ${disabledAttrs} data-product-id="${product.id}" data-variant-id="${variant?.id ?? ''}" data-hasnullprice="${hasNullPrice}" data-available="${product.available}" data-handle="${handleStr}">
          <span class="colour-stitching__swatch-thumb">${img}</span>
        </div>`;

      html += el;
    });

    if (intraVariantSwatches instanceof HTMLElement && html) {
      intraVariantSwatches.classList.add('hidden');
    }

    if (html) this.container.innerHTML = html;

    const swatches = this.container.querySelectorAll('.colour-stitching__swatch-item');
    const scopeCard = this.closest('product-card');
    swatches.forEach((swatch) => {
      const activate = (event) => {
        event.preventDefault();
        event.stopPropagation();
        // Out-of-stock colourways are visible (struck-through) but not selectable.
        if (swatch.dataset.available === 'false') return;
        /** @type {string | undefined} */
        const stitchHandle = swatch.dataset.handle;
        const mainImageContainer =
          scopeCard?.querySelector(`.card-gallery[data-product-id='${settings.id}']`) ||
          document.querySelector(`.card-gallery[data-product-id='${settings.id}']`);
        if (mainImageContainer instanceof HTMLElement && stitchHandle) {
          selectStitchColourway(mainImageContainer, stitchHandle);
        }

        if (
          scopeCard instanceof HTMLElement &&
          mainImageContainer instanceof HTMLElement &&
          stitchHandle &&
          typeof this._stitchProductsForCard !== 'undefined'
        ) {
          const matched = this._stitchProductsForCard.find(
            (p) => ((p.handle ?? '') + '').toString() === stitchHandle,
          );
          applyStitchNavigationToCard(
            scopeCard,
            mainImageContainer,
            stitchHandle,
            matched,
            settings,
          );
        }

        swatches.forEach((item) => item.classList.remove('selected'));
        swatch.classList.add('selected');
      };
      swatch.addEventListener('click', activate);
      swatch.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') activate(e);
      });
    });

    // Wrap swatches in a slideshow if there are more than the visible count
    this._wrapInSlideshow(products.length);
  }

  /**
   * Wraps swatch items inside a slideshow-component for horizontal scrolling with arrows.
   * Desktop shows 7 items, mobile shows 5. Arrows hide at first/last boundaries.
   * @param {number} totalCount
   */
_wrapInSlideshow(totalCount) {
  const DESKTOP_VISIBLE = 7;
  const isMobile = window.innerWidth < 750;

  // Mobile: no slideshow, native scroll handles it via CSS
  if (isMobile) return;

  // Desktop: only activate if more than 7 swatches
  if (totalCount <= DESKTOP_VISIBLE) return;

  const visibleCount = DESKTOP_VISIBLE;
  const grid = this.container;
    if (!grid) return;

    const swatchItems = Array.from(grid.querySelectorAll('.colour-stitching__swatch-item'));
    if (swatchItems.length === 0) return;

    // Build slideshow-slide elements wrapping each swatch
    let slidesHtml = '';
    swatchItems.forEach((swatch, i) => {
      slidesHtml += `<slideshow-slide ref="slides[]" aria-hidden="${i === 0 ? 'false' : 'true'}" style="--slideshow-timeline: --slide-${i};" class="colour-stitching__slide" slide-id="stitch-${i}">${swatch.outerHTML}</slideshow-slide>`;
    });

    // Chevron SVG for the circular arrow buttons
    // const chevronSvg = `<svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 3L9.5 7L5.5 11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const chevronSvg = `<svg class="stitching-icon" xmlns="http://www.w3.org/2000/svg" width="9" height="17" viewBox="0 0 9 17" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.251028 0.25105C0.58578 -0.0836832 1.12852 -0.0836832 1.46322 0.25105L8.61512 7.40294C9.01679 7.80461 9.01679 8.45587 8.61512 8.85754L1.46322 16.0094C1.12852 16.3441 0.58578 16.3441 0.251028 16.0094C-0.083676 15.6747 -0.083676 15.132 0.251028 14.7972L6.91804 8.13024L0.251028 1.46323C-0.083676 1.12848 -0.083676 0.585782 0.251028 0.25105Z" fill="black"/></svg>`;

    // Build timeline-scope
    const timelineScopes = [];
    for (let i = 0; i < swatchItems.length; i++) {
      timelineScopes.push(`--slide-${i}`);
    }

    const settings = readSettings(this);
    const activeHandle = settings?.handle;
    let activeIndex = 0;
    if (activeHandle) {
      activeIndex = swatchItems.findIndex(item => item.dataset.handle === activeHandle);
      if (activeIndex === -1) activeIndex = 0;
    }

    // Build complete slideshow structure with inline arrows (Figma style)
    const slideshowHtml = `
      <div class="colour-stitching__slider-wrapper">
        <button
          class="colour-stitching__circle-arrow colour-stitching__circle-arrow--prev"
          aria-label="Previous"
          data-stitch-arrow="prev"
          disabled
        >
          <span class="colour-stitching__circle-arrow-icon">${chevronSvg}</span>
        </button>
        <slideshow-component
          class="colour-stitching__slideshow"
          style="--slideshow-timeline: ${timelineScopes.join(', ')};"
          initial-slide="${activeIndex}"
        >
          <slideshow-container ref="slideshowContainer">
            <slideshow-slides tabindex="-1" ref="scroller">
              ${slidesHtml}
            </slideshow-slides>
          </slideshow-container>
        </slideshow-component>
        <button
          class="colour-stitching__circle-arrow colour-stitching__circle-arrow--next"
          aria-label="Next"
          data-stitch-arrow="next"
        >
          <span class="colour-stitching__circle-arrow-icon">${chevronSvg}</span>
        </button>
      </div>
    `;

    grid.innerHTML = slideshowHtml;
    grid.style.setProperty('--stitch-visible-count', visibleCount);

    // Wire up the custom circle arrow buttons to the slideshow-component
    const slideshowEl = grid.querySelector('slideshow-component');
    const prevBtn = grid.querySelector('[data-stitch-arrow="prev"]');
    const nextBtn = grid.querySelector('[data-stitch-arrow="next"]');

    if (slideshowEl && prevBtn && nextBtn) {
      const updateArrows = () => {
        requestAnimationFrame(() => {
          const scroller = slideshowEl.querySelector('slideshow-slides');
          if (!scroller) return;
          const { scrollLeft, scrollWidth, clientWidth } = scroller;
          prevBtn.disabled = scrollLeft <= 2;
          nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
        });
      };

      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        /** @type {any} */ (slideshowEl).next?.();
        setTimeout(updateArrows, 150);
      });

      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        /** @type {any} */ (slideshowEl).previous?.();
        setTimeout(updateArrows, 150);
      });

      // Listen for slideshow scroll events to update arrow state
      slideshowEl.addEventListener('slideshow:select', updateArrows);
      const scroller = slideshowEl.querySelector('slideshow-slides');
      if (scroller) {
        scroller.addEventListener('scrollend', updateArrows);
        scroller.addEventListener('scroll', () => {
          clearTimeout(this._scrollTimer);
          this._scrollTimer = setTimeout(updateArrows, 100);
        });
      }

      // Initial arrow state
      setTimeout(updateArrows, 400);
    }

    // Re-bind swatch click events inside the slideshow (only for non-link elements)
    const newSwatches = grid.querySelectorAll('.colour-stitching__swatch-item');
    const isLink = newSwatches[0]?.tagName.toLowerCase() === 'a';

    if (!isLink) {
      const scopeCard = this.closest('product-card');
      newSwatches.forEach((swatch) => {
        const activate = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (swatch.dataset.available === 'false') return;
          const stitchHandle = swatch.dataset.handle;
          const mainImageContainer =
            scopeCard?.querySelector(`.card-gallery[data-product-id='${settings?.id}']`) ||
            document.querySelector(`.card-gallery[data-product-id='${settings?.id}']`);
          if (mainImageContainer instanceof HTMLElement && stitchHandle) {
            selectStitchColourway(mainImageContainer, stitchHandle);
          }
          if (
            scopeCard instanceof HTMLElement &&
            mainImageContainer instanceof HTMLElement &&
            stitchHandle &&
            typeof this._stitchProductsForCard !== 'undefined'
          ) {
            const matched = this._stitchProductsForCard.find(
              (p) => ((p.handle ?? '') + '').toString() === stitchHandle,
            );
            applyStitchNavigationToCard(scopeCard, mainImageContainer, stitchHandle, matched, settings);
          }
          newSwatches.forEach((item) => item.classList.remove('selected'));
          swatch.classList.add('selected');
        };
        swatch.addEventListener('click', activate);
        swatch.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') activate(e);
        });
      });
    }
  }


  /**
   * @param {Record<string, unknown>} product
   * @param {Record<string, unknown>} settings
   */
  getProductImage(product, settings) {
    let imageSrc = productFeaturedSrc(product);

    switch (settings.image_type) {
      case 'featured_image':
        imageSrc = productFeaturedSrc(product);
        break;
      case 'first_variant_image': {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        /** @type {Record<string, unknown> | undefined} */
        const v0 = variants[0];
        if (v0?.image && typeof v0.image === 'object' && /** @type {{ src?: string }} */ (v0.image).src) {
          imageSrc = /** @type {{ src: string }} */ (v0.image).src;
        } else if (v0?.image && typeof v0.image === 'string') {
          imageSrc = /** @type {string} */ (v0.image);
        }
        break;
      }
      case 'last_variant_image': {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const last = variants[variants.length - 1];
        if (last?.image && typeof last.image === 'object' && /** @type {{ src?: string }} */ (last.image).src) {
          imageSrc = /** @type {{ src: string }} */ (last.image).src;
        }
        break;
      }
      default:
        break;
    }

    return `<img src="${imageSrc}" data-image-second="" alt="" width="40" height="40" loading="lazy" decoding="async" />`;
  }
}

customElements.define('colour-swatches-stitch-plp', ColourSwatches);

class ColourSwatchesProduct extends ColourSwatches {
  constructor() {
    super();
  }
    // this.container = this.querySelector('.colour-stitching__container');
    // if (!this.container || this.classList.contains('locked')) return;

    connectedCallback() {
      if (this._initialized) return;
      // Remove 'locked' in case the element was cloned from a currently fetching element.
      this.classList.remove('locked');

    waitForSettings(this).then((settings) => {
      // if (!this.container) return;
      this.container = this.querySelector('.colour-stitching__container');
      if (!this.container || this.classList.contains('locked') || this._initialized) return;
      if (settings == null) {
        console.warn('colour-stitch-pdp: missing JSON settings');
        return;
      }

      this.classList.add('locked');
      this._initialized = true;

      fetchStitchingJson(settings)
        .then((responseJson) => {
          this.container.classList.add('loaded');
          if (!Array.isArray(responseJson)) {
            responseJson = responseJson.products;
          }
          if (!responseJson || responseJson.length < 1) {
            this.classList.add('hidden');
            this.nextElementSibling?.classList?.remove('hidden');
          } else {
            if (window.location.href.search('/products') === -1) {
              this.nextElementSibling?.classList?.add('hidden');
            }
            if (this.closest('.ui-test-compare-products')) {
              this._stitchProductsForCard = Array.isArray(responseJson) ? responseJson : [];
              this.initMainImages(responseJson, settings);
              super.initSwatches(responseJson, settings);
            } else {
              this.initSwatches(responseJson, settings);
              this.setCurrent();
            }
          }
          this.classList.remove('locked');
        })
        .catch((e) => {
          console.warn(e);
          this.classList.add('hidden');
          this.classList.remove('locked');
          this._initialized = false;
        });
    });
  }

  /** @param {Record<string, unknown>[]} products @param {Record<string, unknown>} settings */
  initSwatches(products, settings) {
    let html = '';
    const currentHandle = settings?.handle;
    for (const product of products) {
      const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
      const variant = variants[0];
      const img = this.getProductImage(product, settings);
      /** @type {string} */
      const handleStr = ((product.handle || '') + '').toString();
      const isSelectedClass = currentHandle === handleStr ? 'selected' : '';
      const hasNullPrice = product.price === 0;
      let url = typeof product.url === 'string' ? product.url : '#';
      const card = this.closest('product-card');
      const originalUrlAttr = card ? card.getAttribute('data-stitch-original-url') : '';
      if (url !== '#' && originalUrlAttr && originalUrlAttr.startsWith('/collections/')) {
        const match = originalUrlAttr.match(/^(\/collections\/[^\/]+)/);
        if (match) {
          url = match[1] + url;
        }
      }
      const isAvailable = product.available !== false && product.available !== 'false';
      const disabledAttrs = isAvailable ? '' : ' tabindex="-1" aria-disabled="true"';
      html += `<a class="colour-stitching__swatch-item colour-stitching__swatch-link ${isSelectedClass}"${disabledAttrs} data-product-id="${product.id}" data-variant-id="${variant?.id ?? ''}" data-hasnullprice="${hasNullPrice}" data-available="${product.available}" data-handle="${handleStr}" href="${url}" title="${handleStr.replace(/"/g, '&quot;')}">${img}</a>`;
    }
    if (html) this.container.innerHTML = html;
    this._wrapInSlideshow(products.length);
  }

  /** @param {Record<string, unknown>} product @param {Record<string, unknown>} settings */
  getProductImage(product, settings) {
    let imageSrc = productFeaturedSrc(product);

    switch (settings.image_type) {
      case 'featured_image':
        imageSrc = productFeaturedSrc(product);
        break;
      case 'first_variant_image': {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const first = variants[0];
        if (first?.image && typeof first.image === 'object' && /** @type {{ src?: string }} */ (first.image).src) {
          imageSrc = /** @type {{ src: string }} */ (first.image).src;
        } else if (first?.image && typeof first.image === 'string') {
          imageSrc = /** @type {string} */ (first.image);
        }
        break;
      }
      case 'last_variant_image': {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const last = variants[variants.length - 1];
        if (last?.image && typeof last.image === 'object' && /** @type {{ src?: string }} */ (last.image).src) {
          imageSrc = /** @type {{ src: string }} */ (last.image).src;
        }
        break;
      }
      default:
        break;
    }

    return `<img src="${imageSrc}" alt="" loading="lazy" decoding="async" width="44" height="44" />`;
  }

  setCurrent() {
    const handle =
      window.current?.product?.handle ||
      (typeof window.theme?.currentProduct === 'string' ? window.theme.currentProduct : '');
    if (!handle || !this.container) return;
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const current = /** @type {HTMLAnchorElement | null} */ (
      this.container.querySelector(`a[data-handle="${escaped}"]`)
    );
    current?.classList.add('selected');
  }
}

customElements.define('colour-swatches-stitch-product', ColourSwatchesProduct);

// /**
//  * Colour stitching (group tag siblings): matches legacy SAL ColourSwatches / ColourSwatchesProduct.
//  * Loads sibling products via /collections/all/{groupTag}?view=json or single product ?view=json.
//  */

// function stitchingRoutes() {
//   const r = typeof Theme !== 'undefined' ? Theme.routes : {};
//   const root_url = r.root_url || '';
//   /** @type {string} */
//   let all_products = r.all_products_collection_url || '/collections/all';
//   if (all_products.endsWith('/')) {
//     all_products = all_products.slice(0, -1);
//   }
//   return { root_url, all_products_collection_url: all_products };
// }

// function variantImageSrc(variant) {
//   if (!variant) return '';
//   if (variant.featured_image?.src) return variant.featured_image.src;
//   const im = variant.image;
//   if (typeof im === 'string') return im;
//   return im?.src || '';
// }

// function productFeaturedSrc(product) {
//   const fi = product.featured_image;
//   if (typeof fi === 'string') return fi;
//   return fi?.src || '';
// }

// /** Liquid JSON image fields (`featured_image`, `second_image`) can be a string URL or `{ src }`. */
// function productImageSrcFromLiquidJson(img) {
//   if (img == null) return '';
//   if (typeof img === 'string') return img;
//   if (typeof img === 'object' && 'src' in img && typeof /** @type {{ src?: string }} */ (img).src === 'string') {
//     return /** @type {{ src: string }} */ (img).src;
//   }
//   return '';
// }

// /**
//  * Shopify CDN URLs often differ only by query; treat same path as one image.
//  * @param {string} url
//  */
// function imagePathKey(url) {
//   if (!url) return '';
//   try {
//     const u =
//       url.startsWith('//') ? new URL(`https:${url}`)
//       : /^https?:\/\//i.test(url) ? new URL(url)
//       : new URL(url, 'https://cdn.shopify.com');
//     return `${u.hostname}${u.pathname}`;
//   } catch {
//     return url.split(/[?#]/)[0];
//   }
// }

// /**
//  * Second PLP hover image from `templates/collection.json.liquid` (`second_image` / `images[1]`).
//  * @param {Record<string, unknown>} product
//  * @param {string} primarySrc
//  */
// function productSecondHoverSrc(product, primarySrc) {
//   let s = productImageSrcFromLiquidJson(product.second_image);
//   if (!s || imagePathKey(s) === imagePathKey(primarySrc)) return '';
//   return s;
// }

// function fetchStitchingJson(settings) {
//   const { all_products_collection_url } = stitchingRoutes();
//   if (!settings.groupTag || settings.groupTag === 'undefined') {
//     return Promise.reject(new Error('colour-stitching: missing groupTag'));
//   }
//   const url = `${all_products_collection_url}/${encodeURIComponent(settings.groupTag)}?view=json`;
//   return fetch(url).then((response) => response.json());
// }

// function readSettings(self) {
//   const script = self.querySelector('script[type="application/json"][data-colour-stitch-settings]');
//   if (!script?.textContent) return null;
//   try {
//     return JSON.parse(script.textContent);
//   } catch {
//     return null;
//   }
// }

// function waitForSettings(self) {
//   return new Promise((resolve) => {
//     const interval = setInterval(() => {
//       const s = readSettings(self);
//       if (s) {
//         clearInterval(interval);
//         resolve(s);
//       }
//     }, 100);
//     setTimeout(() => {
//       clearInterval(interval);
//       resolve(readSettings(self));
//     }, 3000);
//   });
// }

// function findCardGallery(settings) {
//   return document.querySelector(`.card-gallery[data-product-id="${settings.id}"]`);
// }

// /**
//  * Instant colourway switch — show new before hiding old so nothing flashes underneath.
//  *
//  * @param {HTMLElement} gallery
//  * @param {string} [stitchHandle]
//  * @returns {boolean}
//  */
// function selectStitchColourway(gallery, stitchHandle) {
//   /** @type {HTMLElement | null} */
//   let next = null;

//   if (stitchHandle) {
//     const esc =
//       typeof CSS !== 'undefined' && CSS.escape
//         ? CSS.escape(stitchHandle)
//         : stitchHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
//     const found = gallery.querySelector(`.colour-stitching__colourway[data-stitch-handle="${esc}"]`);
//     if (found instanceof HTMLElement) next = found;
//   }

//   if (!next) {
//     const fallback = gallery.querySelector('.colour-stitching__colourway');
//     if (fallback instanceof HTMLElement) next = fallback;
//   }

//   if (!next) return false;

//   const prev = gallery.querySelector('.colour-stitching__colourway.selected');
//   if (prev === next) return true;

//   next.classList.add('selected');
//   if (prev instanceof HTMLElement) prev.classList.remove('selected');
//   return true;
// }

// /**
//  * @param {Record<string, unknown> | undefined} product
//  * @param {string} stitchHandle
//  * @param {string} baseHandle
//  * @param {string | null} originalUrlAttr
//  */
// function resolveStitchedCardHref(product, stitchHandle, baseHandle, originalUrlAttr) {
//   const sh = (stitchHandle || '').toString();
//   const bh = (baseHandle || '').toString();
//   if (sh === bh && originalUrlAttr) return originalUrlAttr;

//   const raw = product && typeof product.url === 'string' ? product.url : '';
//   if (!raw) return originalUrlAttr || '';

//   /** @type {string} */
//   let pathPlusSearch;
//   try {
//     const u = raw.startsWith('http') ? new URL(raw) : new URL(raw, window.location.origin);
//     pathPlusSearch = `${u.pathname}${u.search}`;
//   } catch {
//     pathPlusSearch = raw.startsWith('/') ? raw : `/${raw}`;
//   }

//   const variants = product && Array.isArray(product.variants) ? /** @type {Record<string, unknown>[]} */ (product.variants) : [];
//   const vid = variants[0]?.id;
//   if (vid != null && pathPlusSearch && !pathPlusSearch.includes('variant=')) {
//     pathPlusSearch += (pathPlusSearch.includes('?') ? '&' : '?') + `variant=${vid}`;
//   }

//   return pathPlusSearch;
// }

// /**
//  * @param {HTMLElement} card
//  * @param {HTMLElement} gallery
//  * @param {string} stitchHandle
//  * @param {Record<string, unknown> | undefined} product
//  * @param {Record<string, unknown>} settings
//  */
// function applyStitchNavigationToCard(card, gallery, stitchHandle, product, settings) {
//   gallery.dataset.activeStitchHandle = (stitchHandle || '').toString();

//   const baseHandle = ((settings.handle || '') + '').toString();
//   const originalUrlAttr = card.getAttribute('data-stitch-original-url');
//   const path = resolveStitchedCardHref(product, stitchHandle, baseHandle, originalUrlAttr);

//   if (!path) return;

//   let absolute;
//   try {
//     absolute =
//       path.startsWith('http') ? path : new URL(path.startsWith('/') ? path : `/${path}`, window.location.origin).href;
//   } catch {
//     absolute = path;
//   }

//   /** @type {NodeListOf<HTMLAnchorElement> | null} */
//   const mains = card.querySelectorAll('[ref="productCardLink"], a.product-card__link');
//   mains.forEach((a) => {
//     if (a instanceof HTMLAnchorElement) a.href = absolute;
//   });

//   /** @type {HTMLAnchorElement | null} */
//   const galleryLink = gallery.querySelector('a[ref="cardGalleryLink"]');
//   if (galleryLink instanceof HTMLAnchorElement) galleryLink.href = absolute;

//   /** @type {HTMLAnchorElement | null} */
//   const titleLink = card.querySelector('a[ref="productTitleLink"]');
//   if (titleLink instanceof HTMLAnchorElement) titleLink.href = absolute;

//   if (product && card.getAttribute('data-product-transition') === 'true') {
//     if (stitchHandle === baseHandle) {
//       const origFeatured = card.dataset.stitchOrigFeaturedMediaUrl;
//       if (origFeatured) card.setAttribute('data-featured-media-url', origFeatured);
//     } else {
//       const featSrc = productFeaturedSrc(product);
//       if (featSrc) card.setAttribute('data-featured-media-url', featSrc);
//     }
//   }

//   /** @type {{ refs?: { quickAdd?: { fetchProductPage: (url: string) => unknown }}} | null} */
//   const pcAny = /** @type {*} */ (card);
//   pcAny?.refs?.quickAdd?.prefetch?.();
// }

// class ColourSwatches extends HTMLElement {
//   constructor() {
//     super();
//     this.container = this.querySelector('.colour-stitching__container');
//     if (!this.container || this.classList.contains('locked')) return;

//     waitForSettings(this).then((settings) => {
//       if (!this.container) return;
//       if (settings == null) {
//         console.warn('colour-stitch-plp: missing JSON settings');
//         return;
//       }

//       this.classList.add('locked');

//       fetchStitchingJson(settings)
//         .then((responseJson) => {
//           this.container.classList.add('loaded');
//           if (!Array.isArray(responseJson)) {
//             responseJson = responseJson.products;
//           }
//           if (!responseJson || responseJson.length < 1) {
//             this.classList.add('hidden');
//             this.nextElementSibling?.classList.remove('hidden');
//           } else {
//             if (window.location.href.search('/products') === -1) {
//               this.nextElementSibling?.classList.add('hidden');
//             }
//             this._stitchProductsForCard = Array.isArray(responseJson) ? responseJson : [];
//             this.initSwatches(responseJson, settings);
//             this.initMainImages(responseJson, settings);
//           }
//           this.classList.remove('locked');
//         })
//         .catch((e) => {
//           console.warn(e);
//           this.classList.remove('locked');
//         });
//     });
//   }

//   /**
//    * @param {Record<string, unknown>[]} products
//    * @param {Record<string, unknown>} settings
//    */
//   initMainImages(products, settings) {
//     const scope = this.closest('product-card');
//     const gallery =
//       scope?.querySelector(`.card-gallery[data-product-id='${settings.id}']`) || findCardGallery(settings);
//     if (!gallery) return;
//     if (gallery.querySelector('.colour-stitching__image-layer .colour-stitching__colourway')) return;

//     let html = '';
//     for (const product of products) {
//       const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
//       const variant = variants[0];
//       const handleStr = ((product.handle || '') + '').toString();
//       const src = productFeaturedSrc(product) || variantImageSrc(variant);
//       if (!src) continue;
//       const secondSrc = productSecondHoverSrc(/** @type {Record<string, unknown>} */ (product), src);
//       const hasSecond = secondSrc ? 'true' : 'false';
//       const title =
//         variant && typeof variant.title === 'string' ? variant.title : (product.handle || '').toString();
//       const escTitle = title.replace(/"/g, '&quot;');
//       const prim = `<img data-stitch-handle="${handleStr}" data-variant-id="${variant?.id ?? ''}" src="${src}" alt="${escTitle}" class="new-swatch-images new-swatch-images--primary" loading="eager" fetchpriority="low" width="600" height="600" decoding="async" />`;
//       const sec = secondSrc
//         ? `<img data-stitch-handle="${handleStr}" src="${secondSrc}" alt="" class="new-swatch-images new-swatch-images--secondary" loading="eager" fetchpriority="low" width="600" height="600" decoding="async" />`
//         : '';
//       html += `<div class="colour-stitching__colourway" data-stitch-handle="${handleStr}" data-has-second="${hasSecond}">${prim}${sec}</div>`;
//     }

//     /** @type {string} */
//     const currentHandle = ((settings.handle || '') + '').toString();

//     if (!html.trim()) return;

//     if (scope instanceof HTMLElement && scope.getAttribute('data-product-transition') === 'true') {
//       const curFeat = scope.getAttribute('data-featured-media-url');
//       if (curFeat && !scope.dataset.stitchOrigFeaturedMediaUrl) {
//         scope.dataset.stitchOrigFeaturedMediaUrl = curFeat;
//       }
//     }
//     const firstSlideImg = gallery.querySelector('slideshow-slide:not([aria-hidden="true"]) img, slideshow-slide:first-child img');
//     if (firstSlideImg instanceof HTMLImageElement) {
//       firstSlideImg.classList.remove('blur-up', 'fade-in');
//     }

//     const layer =
//       gallery.querySelector('.colour-stitching__image-layer') || document.createElement('div');
//     if (!layer.classList.contains('colour-stitching__image-layer')) {
//       layer.className = 'colour-stitching__image-layer';
//       layer.setAttribute('aria-hidden', 'true');
//       gallery.appendChild(layer);
//     }
//     layer.innerHTML = html;

//     const escapedHandle =
//       typeof CSS !== 'undefined' && CSS.escape
//         ? CSS.escape(currentHandle)
//         : currentHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
//     const selected =
//       currentHandle &&
//       gallery.querySelector(`.colour-stitching__colourway[data-stitch-handle="${escapedHandle}"]`);
//     /** @type {HTMLElement | null | undefined} */
//     const fallback = layer.querySelector('.colour-stitching__colourway');

//     /** @type {HTMLElement | undefined} */
//     let toPick;
//     if (selected instanceof HTMLElement) toPick = selected;
//     else if (fallback instanceof HTMLElement) toPick = fallback;

//     let overlayReady = selectStitchColourway(/** @type {HTMLElement} */ (gallery), currentHandle);
//     if (!overlayReady && toPick instanceof HTMLElement) {
//       layer.querySelectorAll('.colour-stitching__colourway').forEach((n) => n.classList.remove('selected'));
//       toPick.classList.add('selected');
//       overlayReady = true;
//     }

//     if (!overlayReady) return;

//     gallery.classList.add('card-gallery--has-colour-stitching', 'card-gallery--stitch-overlay-ready');
//     if (gallery.hasAttribute('on:pointerenter')) {
//       gallery.classList.add('card-gallery--stitch-respects-hover-preview');
//     }
//     gallery.dataset.cardBaseHandle = currentHandle;
//     gallery.dataset.activeStitchHandle = currentHandle;
//   }

//   /**
//    * @param {Record<string, unknown>[]} products
//    * @param {Record<string, unknown>} settings
//    */
//   initSwatches(products, settings) {
//     let html = '';
//     const cardRoot = this.closest('product-card');
//     const intraVariantSwatches = cardRoot?.querySelector('product-swatches');

//     products.forEach((product) => {
//       const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
//       const variant = variants[0];
//       const img = this.getProductImage(product, settings);
//       /** @type {string} */
//       const handleStr = ((product.handle || '') + '').toString();
//       const isSelectedClass = settings.handle === handleStr ? 'selected' : '';

//       const hasNullPrice = product.price === 0 || product.price === undefined;

//       const isAvailable = product.available !== false && product.available !== 'false';
//       const disabledAttrs = isAvailable ? 'tabindex="0"' : 'tabindex="-1" aria-disabled="true"';
//       const el = `<div class="colour-stitching__swatch-item thumbnail ${isSelectedClass}" role="button" ${disabledAttrs} data-product-id="${product.id}" data-variant-id="${variant?.id ?? ''}" data-hasnullprice="${hasNullPrice}" data-available="${product.available}" data-handle="${handleStr}">
//           <span class="colour-stitching__swatch-thumb">${img}</span>
//         </div>`;

//       html += el;
//     });

//     if (intraVariantSwatches instanceof HTMLElement && html) {
//       intraVariantSwatches.classList.add('hidden');
//     }

//     if (html) this.container.innerHTML = html;

//     const swatches = this.container.querySelectorAll('.colour-stitching__swatch-item');
//     const scopeCard = this.closest('product-card');
//     swatches.forEach((swatch) => {
//       const activate = (event) => {
//         event.preventDefault();
//         event.stopPropagation();
//         // Out-of-stock colourways are visible (struck-through) but not selectable.
//         if (swatch.dataset.available === 'false') return;
//         /** @type {string | undefined} */
//         const stitchHandle = swatch.dataset.handle;
//         const mainImageContainer =
//           scopeCard?.querySelector(`.card-gallery[data-product-id='${settings.id}']`) ||
//           document.querySelector(`.card-gallery[data-product-id='${settings.id}']`);
//         if (mainImageContainer instanceof HTMLElement && stitchHandle) {
//           selectStitchColourway(mainImageContainer, stitchHandle);
//         }

//         if (
//           scopeCard instanceof HTMLElement &&
//           mainImageContainer instanceof HTMLElement &&
//           stitchHandle &&
//           typeof this._stitchProductsForCard !== 'undefined'
//         ) {
//           const matched = this._stitchProductsForCard.find(
//             (p) => ((p.handle ?? '') + '').toString() === stitchHandle,
//           );
//           applyStitchNavigationToCard(
//             scopeCard,
//             mainImageContainer,
//             stitchHandle,
//             matched,
//             settings,
//           );
//         }

//         swatches.forEach((item) => item.classList.remove('selected'));
//         swatch.classList.add('selected');
//       };
//       swatch.addEventListener('click', activate);
//       swatch.addEventListener('keydown', (e) => {
//         if (e.key === 'Enter' || e.key === ' ') activate(e);
//       });
//     });
//   }

//   /**
//    * @param {Record<string, unknown>} product
//    * @param {Record<string, unknown>} settings
//    */
//   getProductImage(product, settings) {
//     let imageSrc = productFeaturedSrc(product);

//     switch (settings.image_type) {
//       case 'featured_image':
//         imageSrc = productFeaturedSrc(product);
//         break;
//       case 'first_variant_image': {
//         const variants = Array.isArray(product.variants) ? product.variants : [];
//         /** @type {Record<string, unknown> | undefined} */
//         const v0 = variants[0];
//         if (v0?.image && typeof v0.image === 'object' && /** @type {{ src?: string }} */ (v0.image).src) {
//           imageSrc = /** @type {{ src: string }} */ (v0.image).src;
//         } else if (v0?.image && typeof v0.image === 'string') {
//           imageSrc = /** @type {string} */ (v0.image);
//         }
//         break;
//       }
//       case 'last_variant_image': {
//         const variants = Array.isArray(product.variants) ? product.variants : [];
//         const last = variants[variants.length - 1];
//         if (last?.image && typeof last.image === 'object' && /** @type {{ src?: string }} */ (last.image).src) {
//           imageSrc = /** @type {{ src: string }} */ (last.image).src;
//         }
//         break;
//       }
//       default:
//         break;
//     }

//     return `<img src="${imageSrc}" data-image-second="" alt="" width="40" height="40" loading="lazy" decoding="async" />`;
//   }
// }

// customElements.define('colour-swatches-stitch-plp', ColourSwatches);

// class ColourSwatchesProduct extends HTMLElement {
//   constructor() {
//     super();

//     this.container = this.querySelector('.colour-stitching__container');
//     if (!this.container || this.classList.contains('locked')) return;

//     waitForSettings(this).then((settings) => {
//       if (!this.container) return;
//       if (settings == null) {
//         console.warn('colour-stitch-pdp: missing JSON settings');
//         return;
//       }

//       this.classList.add('locked');

//       fetchStitchingJson(settings)
//         .then((responseJson) => {
//           this.container.classList.add('loaded');
//           if (!Array.isArray(responseJson)) {
//             responseJson = responseJson.products;
//           }
//           if (!responseJson || responseJson.length < 1) {
//             this.classList.add('hidden');
//             this.nextElementSibling?.classList?.remove('hidden');
//           } else {
//             if (window.location.href.search('/products') === -1) {
//               this.nextElementSibling?.classList?.add('hidden');
//             }
//             this.initSwatches(responseJson, settings);
//             this.setCurrent();
//           }
//           this.classList.remove('locked');
//         })
//         .catch((e) => {
//           console.warn(e);
//           this.classList.remove('locked');
//         });
//     });
//   }

//   /** @param {Record<string, unknown>[]} products @param {Record<string, unknown>} settings */
//   initSwatches(products, settings) {
//     let html = '';
//     for (const product of products) {
//       const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
//       const variant = variants[0];
//       const img = this.getProductImage(product, settings);
//       /** @type {string} */
//       const handleStr = ((product.handle || '') + '').toString();
//       const hasNullPrice = product.price === 0;
//       const url = typeof product.url === 'string' ? product.url : '#';
//       const isAvailable = product.available !== false && product.available !== 'false';
//       const disabledAttrs = isAvailable ? '' : ' tabindex="-1" aria-disabled="true"';
//       html += `<a class="colour-stitching__swatch-item colour-stitching__swatch-link"${disabledAttrs} data-product-id="${product.id}" data-variant-id="${variant?.id ?? ''}" data-hasnullprice="${hasNullPrice}" data-available="${product.available}" data-handle="${handleStr}" href="${url}" title="${handleStr.replace(/"/g, '&quot;')}">${img}</a>`;
//     }
//     if (html) this.container.innerHTML = html;
//   }

//   /** @param {Record<string, unknown>} product @param {Record<string, unknown>} settings */
//   getProductImage(product, settings) {
//     let imageSrc = productFeaturedSrc(product);

//     switch (settings.image_type) {
//       case 'featured_image':
//         imageSrc = productFeaturedSrc(product);
//         break;
//       case 'first_variant_image': {
//         const variants = Array.isArray(product.variants) ? product.variants : [];
//         const first = variants[0];
//         if (first?.image && typeof first.image === 'object' && /** @type {{ src?: string }} */ (first.image).src) {
//           imageSrc = /** @type {{ src: string }} */ (first.image).src;
//         } else if (first?.image && typeof first.image === 'string') {
//           imageSrc = /** @type {string} */ (first.image);
//         }
//         break;
//       }
//       case 'last_variant_image': {
//         const variants = Array.isArray(product.variants) ? product.variants : [];
//         const last = variants[variants.length - 1];
//         if (last?.image && typeof last.image === 'object' && /** @type {{ src?: string }} */ (last.image).src) {
//           imageSrc = /** @type {{ src: string }} */ (last.image).src;
//         }
//         break;
//       }
//       default:
//         break;
//     }

//     return `<img src="${imageSrc}" alt="" loading="lazy" decoding="async" width="44" height="44" />`;
//   }

//   setCurrent() {
//     const handle =
//       window.current?.product?.handle ||
//       (typeof window.theme?.currentProduct === 'string' ? window.theme.currentProduct : '');
//     if (!handle || !this.container) return;
//     const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
//     const current = /** @type {HTMLAnchorElement | null} */ (
//       this.container.querySelector(`a[data-handle="${escaped}"]`)
//     );
//     current?.classList.add('selected');
//   }
// }

// customElements.define('colour-swatches-stitch-product', ColourSwatchesProduct);


/**
 * Colour stitching (group tag siblings): matches legacy SAL ColourSwatches / ColourSwatchesProduct.
 * Loads sibling products via /collections/all/{groupTag}?view=json or single product ?view=json.
 */








// -------------------------------- Different Comment (Comment 2)--------------------------------

// /**
//  * Colour stitching (group tag siblings): matches legacy SAL ColourSwatches / ColourSwatchesProduct.
//  * Loads sibling products via /collections/all/{groupTag}?view=json or single product ?view=json.
//  */

// function stitchingRoutes() {
//   const r = typeof Theme !== 'undefined' ? Theme.routes : {};
//   const root_url = r.root_url || '';
//   /** @type {string} */
//   let all_products = r.all_products_collection_url || '/collections/all';
//   if (all_products.endsWith('/')) {
//     all_products = all_products.slice(0, -1);
//   }
//   return { root_url, all_products_collection_url: all_products };
// }

// function variantImageSrc(variant) {
//   if (!variant) return '';
//   if (variant.featured_image?.src) return variant.featured_image.src;
//   const im = variant.image;
//   if (typeof im === 'string') return im;
//   return im?.src || '';
// }

// function productFeaturedSrc(product) {
//   const fi = product.featured_image;
//   if (typeof fi === 'string') return fi;
//   return fi?.src || '';
// }

// /** Liquid JSON image fields (`featured_image`, `second_image`) can be a string URL or `{ src }`. */
// function productImageSrcFromLiquidJson(img) {
//   if (img == null) return '';
//   if (typeof img === 'string') return img;
//   if (typeof img === 'object' && 'src' in img && typeof /** @type {{ src?: string }} */ (img).src === 'string') {
//     return /** @type {{ src: string }} */ (img).src;
//   }
//   return '';
// }

// /**
//  * Shopify CDN URLs often differ only by query; treat same path as one image.
//  * @param {string} url
//  */
// function imagePathKey(url) {
//   if (!url) return '';
//   try {
//     const u =
//       url.startsWith('//') ? new URL(`https:${url}`)
//       : /^https?:\/\//i.test(url) ? new URL(url)
//       : new URL(url, 'https://cdn.shopify.com');
//     return `${u.hostname}${u.pathname}`;
//   } catch {
//     return url.split(/[?#]/)[0];
//   }
// }

// /**
//  * Second PLP hover image from `templates/collection.json.liquid` (`second_image` / `images[1]`).
//  * @param {Record<string, unknown>} product
//  * @param {string} primarySrc
//  */
// function productSecondHoverSrc(product, primarySrc) {
//   let s = productImageSrcFromLiquidJson(product.second_image);
//   if (!s || imagePathKey(s) === imagePathKey(primarySrc)) return '';
//   return s;
// }

// function fetchStitchingJson(settings) {
//   const { all_products_collection_url } = stitchingRoutes();
//   if (!settings.groupTag || settings.groupTag === 'undefined') {
//     return Promise.reject(new Error('colour-stitching: missing groupTag'));
//   }

//   const fetchTag = (tag) =>
//     fetch(`${all_products_collection_url}/${encodeURIComponent(tag)}?view=json`)
//       .then((response) => response.json())
//       .then((json) => (Array.isArray(json) ? json : json?.products || []))
//       .catch(() => []);

//   // Fetch both the tag found on the product and its alternate spelling
//   // (group: <-> gruop: typo), then merge so mixed-tagged groups stay complete.
//   const tags = [settings.groupTag];
//   if (settings.groupTagAlt && settings.groupTagAlt !== 'undefined' && settings.groupTagAlt !== settings.groupTag) {
//     tags.push(settings.groupTagAlt);
//   }

//   return Promise.all(tags.map(fetchTag)).then((lists) => {
//     const seen = new Set();
//     const merged = [];
//     for (const list of lists) {
//       for (const p of list) {
//         const key = `${p?.handle ?? p?.id ?? ''}`;
//         if (!key || seen.has(key)) continue;
//         seen.add(key);
//         merged.push(p);
//       }
//     }
//     return merged;
//   });
// }

// function readSettings(self) {
//   const script = self.querySelector('script[type="application/json"][data-colour-stitch-settings]');
//   if (!script?.textContent) return null;
//   try {
//     return JSON.parse(script.textContent);
//   } catch {
//     return null;
//   }
// }

// function waitForSettings(self) {
//   return new Promise((resolve) => {
//     const interval = setInterval(() => {
//       const s = readSettings(self);
//       if (s) {
//         clearInterval(interval);
//         resolve(s);
//       }
//     }, 100);
//     setTimeout(() => {
//       clearInterval(interval);
//       resolve(readSettings(self));
//     }, 3000);
//   });
// }

// function findCardGallery(settings) {
//   return document.querySelector(`.card-gallery[data-product-id="${settings.id}"]`);
// }

// /**
//  * Instant colourway switch — show new before hiding old so nothing flashes underneath.
//  *
//  * @param {HTMLElement} gallery
//  * @param {string} [stitchHandle]
//  * @returns {boolean}
//  */
// function selectStitchColourway(gallery, stitchHandle) {
//   /** @type {HTMLElement | null} */
//   let next = null;

//   if (stitchHandle) {
//     const esc =
//       typeof CSS !== 'undefined' && CSS.escape
//         ? CSS.escape(stitchHandle)
//         : stitchHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
//     const found = gallery.querySelector(`.colour-stitching__colourway[data-stitch-handle="${esc}"]`);
//     if (found instanceof HTMLElement) next = found;
//   }

//   if (!next) {
//     const fallback = gallery.querySelector('.colour-stitching__colourway');
//     if (fallback instanceof HTMLElement) next = fallback;
//   }

//   if (!next) return false;

//   const prev = gallery.querySelector('.colour-stitching__colourway.selected');
//   if (prev === next) return true;

//   next.classList.add('selected');
//   if (prev instanceof HTMLElement) prev.classList.remove('selected');
//   return true;
// }

// /**
//  * @param {Record<string, unknown> | undefined} product
//  * @param {string} stitchHandle
//  * @param {string} baseHandle
//  * @param {string | null} originalUrlAttr
//  */
// function resolveStitchedCardHref(product, stitchHandle, baseHandle, originalUrlAttr) {
//   const sh = (stitchHandle || '').toString();
//   const bh = (baseHandle || '').toString();
//   if (sh === bh && originalUrlAttr) return originalUrlAttr;

//   const raw = product && typeof product.url === 'string' ? product.url : '';
//   if (!raw) return originalUrlAttr || '';

//   /** @type {string} */
//   let pathPlusSearch;
//   try {
//     const u = raw.startsWith('http') ? new URL(raw) : new URL(raw, window.location.origin);
//     pathPlusSearch = `${u.pathname}${u.search}`;
//   } catch {
//     pathPlusSearch = raw.startsWith('/') ? raw : `/${raw}`;
//   }

//   const variants = product && Array.isArray(product.variants) ? /** @type {Record<string, unknown>[]} */ (product.variants) : [];
//   const vid = variants[0]?.id;
//   if (vid != null && pathPlusSearch && !pathPlusSearch.includes('variant=')) {
//     pathPlusSearch += (pathPlusSearch.includes('?') ? '&' : '?') + `variant=${vid}`;
//   }

//   return pathPlusSearch;
// }

// /**
//  * @param {HTMLElement} card
//  * @param {HTMLElement} gallery
//  * @param {string} stitchHandle
//  * @param {Record<string, unknown> | undefined} product
//  * @param {Record<string, unknown>} settings
//  */
// function applyStitchNavigationToCard(card, gallery, stitchHandle, product, settings) {
//   gallery.dataset.activeStitchHandle = (stitchHandle || '').toString();

//   const baseHandle = ((settings.handle || '') + '').toString();
//   const originalUrlAttr = card.getAttribute('data-stitch-original-url');
//   const path = resolveStitchedCardHref(product, stitchHandle, baseHandle, originalUrlAttr);

//   if (!path) return;

//   let absolute;
//   try {
//     absolute =
//       path.startsWith('http') ? path : new URL(path.startsWith('/') ? path : `/${path}`, window.location.origin).href;
//   } catch {
//     absolute = path;
//   }

//   /** @type {NodeListOf<HTMLAnchorElement> | null} */
//   const mains = card.querySelectorAll('[ref="productCardLink"], a.product-card__link');
//   mains.forEach((a) => {
//     if (a instanceof HTMLAnchorElement) a.href = absolute;
//   });

//   /** @type {HTMLAnchorElement | null} */
//   const galleryLink = gallery.querySelector('a[ref="cardGalleryLink"]');
//   if (galleryLink instanceof HTMLAnchorElement) galleryLink.href = absolute;

//   /** @type {HTMLAnchorElement | null} */
//   const titleLink = card.querySelector('a[ref="productTitleLink"]');
//   if (titleLink instanceof HTMLAnchorElement) titleLink.href = absolute;

//   if (product && card.getAttribute('data-product-transition') === 'true') {
//     if (stitchHandle === baseHandle) {
//       const origFeatured = card.dataset.stitchOrigFeaturedMediaUrl;
//       if (origFeatured) card.setAttribute('data-featured-media-url', origFeatured);
//     } else {
//       const featSrc = productFeaturedSrc(product);
//       if (featSrc) card.setAttribute('data-featured-media-url', featSrc);
//     }
//   }

//   /** @type {{ refs?: { quickAdd?: { fetchProductPage: (url: string) => unknown }}} | null} */
//   const pcAny = /** @type {*} */ (card);
//   pcAny?.refs?.quickAdd?.prefetch?.();
// }

// class ColourSwatches extends HTMLElement {
//   constructor() {
//     super();
//     this.container = this.querySelector('.colour-stitching__container');
//     if (!this.container || this.classList.contains('locked')) return;

//     waitForSettings(this).then((settings) => {
//       if (!this.container) return;
//       if (settings == null) {
//         console.warn('colour-stitch-plp: missing JSON settings');
//         return;
//       }

//       this.classList.add('locked');

//       fetchStitchingJson(settings)
//         .then((responseJson) => {
//           this.container.classList.add('loaded');
//           if (!Array.isArray(responseJson)) {
//             responseJson = responseJson.products;
//           }
//           if (!responseJson || responseJson.length <= 1) {
//             this.classList.add('hidden');
//             this.nextElementSibling?.classList.remove('hidden');
//           } else {
//             if (window.location.href.search('/products') === -1) {
//               this.nextElementSibling?.classList.add('hidden');
//             }
//             this._stitchProductsForCard = Array.isArray(responseJson) ? responseJson : [];
//             this.initSwatches(responseJson, settings);
//             this.initMainImages(responseJson, settings);
//           }
//           this.classList.remove('locked');
//         })
//         .catch((e) => {
//           console.warn(e);
//           this.classList.remove('locked');
//         });
//     });
//   }

//   /**
//    * @param {Record<string, unknown>[]} products
//    * @param {Record<string, unknown>} settings
//    */
//   initMainImages(products, settings) {
//     const scope = this.closest('product-card');
//     const gallery =
//       scope?.querySelector(`.card-gallery[data-product-id='${settings.id}']`) || findCardGallery(settings);
//     if (!gallery) return;
//     if (gallery.querySelector('.colour-stitching__image-layer .colour-stitching__colourway')) return;

//     let html = '';
//     for (const product of products) {
//       const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
//       const variant = variants[0];
//       const handleStr = ((product.handle || '') + '').toString();
//       const src = productFeaturedSrc(product) || variantImageSrc(variant);
//       if (!src) continue;
//       const secondSrc = productSecondHoverSrc(/** @type {Record<string, unknown>} */ (product), src);
//       const hasSecond = secondSrc ? 'true' : 'false';
//       const title =
//         variant && typeof variant.title === 'string' ? variant.title : (product.handle || '').toString();
//       const escTitle = title.replace(/"/g, '&quot;');
//       const prim = `<img data-stitch-handle="${handleStr}" data-variant-id="${variant?.id ?? ''}" src="${src}" alt="${escTitle}" class="new-swatch-images new-swatch-images--primary" loading="eager" fetchpriority="low" width="600" height="600" decoding="async" />`;
//       const sec = secondSrc
//         ? `<img data-stitch-handle="${handleStr}" src="${secondSrc}" alt="" class="new-swatch-images new-swatch-images--secondary" loading="eager" fetchpriority="low" width="600" height="600" decoding="async" />`
//         : '';
//       html += `<div class="colour-stitching__colourway" data-stitch-handle="${handleStr}" data-has-second="${hasSecond}">${prim}${sec}</div>`;
//     }

//     /** @type {string} */
//     const currentHandle = ((settings.handle || '') + '').toString();

//     if (!html.trim()) return;

//     if (scope instanceof HTMLElement && scope.getAttribute('data-product-transition') === 'true') {
//       const curFeat = scope.getAttribute('data-featured-media-url');
//       if (curFeat && !scope.dataset.stitchOrigFeaturedMediaUrl) {
//         scope.dataset.stitchOrigFeaturedMediaUrl = curFeat;
//       }
//     }
//     const firstSlideImg = gallery.querySelector('slideshow-slide:not([aria-hidden="true"]) img, slideshow-slide:first-child img');
//     if (firstSlideImg instanceof HTMLImageElement) {
//       firstSlideImg.classList.remove('blur-up', 'fade-in');
//     }

//     const layer =
//       gallery.querySelector('.colour-stitching__image-layer') || document.createElement('div');
//     if (!layer.classList.contains('colour-stitching__image-layer')) {
//       layer.className = 'colour-stitching__image-layer';
//       layer.setAttribute('aria-hidden', 'true');
//       gallery.appendChild(layer);
//     }
//     layer.innerHTML = html;

//     const escapedHandle =
//       typeof CSS !== 'undefined' && CSS.escape
//         ? CSS.escape(currentHandle)
//         : currentHandle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
//     const selected =
//       currentHandle &&
//       gallery.querySelector(`.colour-stitching__colourway[data-stitch-handle="${escapedHandle}"]`);
//     /** @type {HTMLElement | null | undefined} */
//     const fallback = layer.querySelector('.colour-stitching__colourway');

//     /** @type {HTMLElement | undefined} */
//     let toPick;
//     if (selected instanceof HTMLElement) toPick = selected;
//     else if (fallback instanceof HTMLElement) toPick = fallback;

//     let overlayReady = selectStitchColourway(/** @type {HTMLElement} */ (gallery), currentHandle);
//     if (!overlayReady && toPick instanceof HTMLElement) {
//       layer.querySelectorAll('.colour-stitching__colourway').forEach((n) => n.classList.remove('selected'));
//       toPick.classList.add('selected');
//       overlayReady = true;
//     }

//     if (!overlayReady) return;

//     gallery.classList.add('card-gallery--has-colour-stitching', 'card-gallery--stitch-overlay-ready');
//     if (gallery.hasAttribute('on:pointerenter')) {
//       gallery.classList.add('card-gallery--stitch-respects-hover-preview');
//     }
//     gallery.dataset.cardBaseHandle = currentHandle;
//     gallery.dataset.activeStitchHandle = currentHandle;
//   }

//   /**
//    * @param {Record<string, unknown>[]} products
//    * @param {Record<string, unknown>} settings
//    */
//   initSwatches(products, settings) {
//     let html = '';
//     const cardRoot = this.closest('product-card');
//     const intraVariantSwatches = cardRoot?.querySelector('product-swatches');

//     products.forEach((product) => {
//       const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
//       const variant = variants[0];
//       const img = this.getProductImage(product, settings);
//       /** @type {string} */
//       const handleStr = ((product.handle || '') + '').toString();
//       const isSelectedClass = settings.handle === handleStr ? 'selected' : '';

//       const hasNullPrice = product.price === 0 || product.price === undefined;

//       const el = `<div class="colour-stitching__swatch-item thumbnail ${isSelectedClass}" role="button" tabindex="0" data-product-id="${product.id}" data-variant-id="${variant?.id ?? ''}" data-hasnullprice="${hasNullPrice}" data-available="${product.available}" data-handle="${handleStr}">
//           <span class="colour-stitching__swatch-thumb">${img}</span>
//         </div>`;

//       html += el;
//     });

//     if (intraVariantSwatches instanceof HTMLElement && html) {
//       intraVariantSwatches.classList.add('hidden');
//     }

//     if (html) this.container.innerHTML = html;

//     const swatches = this.container.querySelectorAll('.colour-stitching__swatch-item');
//     const scopeCard = this.closest('product-card');
//     swatches.forEach((swatch) => {
//       const activate = (event) => {
//         event.preventDefault();
//         event.stopPropagation();
//         /** @type {string | undefined} */
//         const stitchHandle = swatch.dataset.handle;
//         const mainImageContainer =
//           scopeCard?.querySelector(`.card-gallery[data-product-id='${settings.id}']`) ||
//           document.querySelector(`.card-gallery[data-product-id='${settings.id}']`);
//         if (mainImageContainer instanceof HTMLElement && stitchHandle) {
//           selectStitchColourway(mainImageContainer, stitchHandle);
//         }

//         if (
//           scopeCard instanceof HTMLElement &&
//           mainImageContainer instanceof HTMLElement &&
//           stitchHandle &&
//           typeof this._stitchProductsForCard !== 'undefined'
//         ) {
//           const matched = this._stitchProductsForCard.find(
//             (p) => ((p.handle ?? '') + '').toString() === stitchHandle,
//           );
//           applyStitchNavigationToCard(
//             scopeCard,
//             mainImageContainer,
//             stitchHandle,
//             matched,
//             settings,
//           );
//         }

//         swatches.forEach((item) => item.classList.remove('selected'));
//         swatch.classList.add('selected');
//       };
//       swatch.addEventListener('click', activate);
//       swatch.addEventListener('keydown', (e) => {
//         if (e.key === 'Enter' || e.key === ' ') activate(e);
//       });
//     });
//   }

//   /**
//    * @param {Record<string, unknown>} product
//    * @param {Record<string, unknown>} settings
//    */
//   getProductImage(product, settings) {
//     let imageSrc = productFeaturedSrc(product);

//     switch (settings.image_type) {
//       case 'featured_image':
//         imageSrc = productFeaturedSrc(product);
//         break;
//       case 'first_variant_image': {
//         const variants = Array.isArray(product.variants) ? product.variants : [];
//         /** @type {Record<string, unknown> | undefined} */
//         const v0 = variants[0];
//         if (v0?.image && typeof v0.image === 'object' && /** @type {{ src?: string }} */ (v0.image).src) {
//           imageSrc = /** @type {{ src: string }} */ (v0.image).src;
//         } else if (v0?.image && typeof v0.image === 'string') {
//           imageSrc = /** @type {string} */ (v0.image);
//         }
//         break;
//       }
//       case 'last_variant_image': {
//         const variants = Array.isArray(product.variants) ? product.variants : [];
//         const last = variants[variants.length - 1];
//         if (last?.image && typeof last.image === 'object' && /** @type {{ src?: string }} */ (last.image).src) {
//           imageSrc = /** @type {{ src: string }} */ (last.image).src;
//         }
//         break;
//       }
//       default:
//         break;
//     }

//     return `<img src="${imageSrc}" data-image-second="" alt="" width="40" height="40" loading="lazy" decoding="async" />`;
//   }
// }

// customElements.define('colour-swatches-stitch-plp', ColourSwatches);

// class ColourSwatchesProduct extends HTMLElement {
//   constructor() {
//     super();

//     this.container = this.querySelector('.colour-stitching__container');
//     if (!this.container || this.classList.contains('locked')) return;

//     waitForSettings(this).then((settings) => {
//       if (!this.container) return;
//       if (settings == null) {
//         console.warn('colour-stitch-pdp: missing JSON settings');
//         return;
//       }

//       this.classList.add('locked');

//       fetchStitchingJson(settings)
//         .then((responseJson) => {
//           this.container.classList.add('loaded');
//           if (!Array.isArray(responseJson)) {
//             responseJson = responseJson.products;
//           }
//           if (!responseJson || responseJson.length <= 1) {
//             this.classList.add('hidden');
//             this.nextElementSibling?.classList?.remove('hidden');
//           } else {
//             if (window.location.href.search('/products') === -1) {
//               this.nextElementSibling?.classList?.add('hidden');
//             }
//             this.initSwatches(responseJson, settings);
//             this.setCurrent();
//           }
//           this.classList.remove('locked');
//         })
//         .catch((e) => {
//           console.warn(e);
//           this.classList.remove('locked');
//         });
//     });
//   }

//   /** @param {Record<string, unknown>[]} products @param {Record<string, unknown>} settings */
//   initSwatches(products, settings) {
//     let html = '';
//     for (const product of products) {
//       const variants = /** @type {Record<string, unknown>[]} */ (product.variants || []);
//       const variant = variants[0];
//       const img = this.getProductImage(product, settings);
//       /** @type {string} */
//       const handleStr = ((product.handle || '') + '').toString();
//       const hasNullPrice = product.price === 0;
//       const url = typeof product.url === 'string' ? product.url : '#';
//       html += `<a class="colour-stitching__swatch-item colour-stitching__swatch-link" data-product-id="${product.id}" data-variant-id="${variant?.id ?? ''}" data-hasnullprice="${hasNullPrice}" data-available="${product.available}" data-handle="${handleStr}" href="${url}" title="${handleStr.replace(/"/g, '&quot;')}">${img}</a>`;
//     }
//     if (html) this.container.innerHTML = html;
//   }

//   /** @param {Record<string, unknown>} product @param {Record<string, unknown>} settings */
//   getProductImage(product, settings) {
//     let imageSrc = productFeaturedSrc(product);

//     switch (settings.image_type) {
//       case 'featured_image':
//         imageSrc = productFeaturedSrc(product);
//         break;
//       case 'first_variant_image': {
//         const variants = Array.isArray(product.variants) ? product.variants : [];
//         const first = variants[0];
//         if (first?.image && typeof first.image === 'object' && /** @type {{ src?: string }} */ (first.image).src) {
//           imageSrc = /** @type {{ src: string }} */ (first.image).src;
//         } else if (first?.image && typeof first.image === 'string') {
//           imageSrc = /** @type {string} */ (first.image);
//         }
//         break;
//       }
//       case 'last_variant_image': {
//         const variants = Array.isArray(product.variants) ? product.variants : [];
//         const last = variants[variants.length - 1];
//         if (last?.image && typeof last.image === 'object' && /** @type {{ src?: string }} */ (last.image).src) {
//           imageSrc = /** @type {{ src: string }} */ (last.image).src;
//         }
//         break;
//       }
//       default:
//         break;
//     }

//     return `<img src="${imageSrc}" alt="" loading="lazy" decoding="async" width="44" height="44" />`;
//   }

//   setCurrent() {
//     const handle =
//       window.current?.product?.handle ||
//       (typeof window.theme?.currentProduct === 'string' ? window.theme.currentProduct : '');
//     if (!handle || !this.container) return;
//     const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
//     const current = /** @type {HTMLAnchorElement | null} */ (
//       this.container.querySelector(`a[data-handle="${escaped}"]`)
//     );
//     current?.classList.add('selected');
//   }
// }

// customElements.define('colour-swatches-stitch-product', ColourSwatchesProduct);
